import { Component, Host, h, Prop, Element, State, Event, EventEmitter, Method } from '@stencil/core';
import { DataService } from '../../utils/data-service.service';

interface ResultItem {
  blog?: boolean;
  news?: boolean;
  category: string;
  date: string;
  headline: string;
  subline: string;
  button_target: string;
  button_text: string;
  image?: {
    fallbackSrc: string;
    alt: string;
    srcset: [string, string][];
  };
}

interface ResponseData {
  showLoadMore: 'false' | 'true';
  error: string[];
  isAuthor: boolean;
  results: ResultItem[];
}

@Component({
  tag: 'bam-load-more',
  styleUrl: 'bam-load-more.scss',
  shadow: false,
})
export class BamLoadMore {
  @Prop() url: string;
  @Prop({ mutable: true }) offset?: number;
  @Prop({ mutable: true }) category?: string;
  @Prop({ mutable: true }) limit?: number;
  @Prop() json_has_pages: boolean;
  @Prop() json_has_images?: boolean;
  @Prop() tag_name_item: string;
  @Prop() tag_name_list: string;
  @Prop() reply_from?: string;
  @Prop() reply_author?: string;
  @Prop() security_message?: string;
  @Prop() hasMoreItems: boolean = false;
  @Element() el: HTMLBamLoadMoreElement;

  @State() pageNumber: number = 1;
  @State() disabled: boolean = false;

  private pageCount: number;
  private btn: HTMLBalButtonElement;
  private allowed: boolean = true;
  private newFirstEl: HTMLElement;

  @Event({ bubbles: true, composed: true }) loadedMore: EventEmitter<boolean>;
  @Event({ bubbles: true, composed: true }) loadedMoreComplete: EventEmitter;

  private toggleBtn = (disable: boolean) => {
    disable ? this.btn.setAttribute('disabled', 'true') : this.btn.removeAttribute('disabled');
    this.allowed = !disable;
  };

  private handleLoadMore = e => {
    e.preventDefault();
    if (this.json_has_pages && this.pageCount > this.pageNumber && this.allowed) {
      const queryChar = this.url.indexOf('?') >= 0 ? '&' : '?';
      this.toggleBtn(true);
      this.pageNumber++;
      const newUrl = `${this.url}${queryChar}currentPage=${this.pageNumber}`;

      this.requestItems(newUrl);
    } else if (!this.json_has_pages && this.allowed) {
      // --- handle offset --- //
      let url = this.url;
      if (this.offset) {
        url = `${this.url}?offset=${this.offset}`;
        this.offset += this.limit ? this.limit : 6;
        if (this.category) {
          url = `${url}&category=${this.category}`;
        }
      }
      this.toggleBtn(true);
      this.requestItems(url);
    }
  };

  @Method()
  async requestItems(url: string) {
    DataService.getData(url)
      .then((data: ResponseData) => {
        if (data.results && data.results.length && data.results.length > 0) {
          const loadMoreListWrapper = this.el.closest('.has-load-more');
          const list = !!loadMoreListWrapper ? loadMoreListWrapper.querySelector(this.tag_name_list) : null;

          if (this.offset) {
            this.disabled = data.showLoadMore !== 'true';
          }

          const hasMasonryGrid = loadMoreListWrapper?.classList.contains('has-masonry-grid');
          data.results.forEach((resp: ResultItem, index) => {
            list !== null && this.renderItem(resp, list, hasMasonryGrid, index);
          });
          if (hasMasonryGrid && list !== null) {
            setTimeout(() => {
              this.loadedMore.emit(true);
            }, 100);
          }
        }
      })
      .then(() => {
        setTimeout(() => {
          if (this.disabled) {
            this.btn.classList.add('hidden');
            this.toggleBtn(true);
          } else {
            this.btn.classList.remove('hidden');
            this.toggleBtn(false);
          }
          this.loadedMoreComplete.emit({ offset: this.offset });
          this.newFirstEl.focus();
        }, 250);
      });
  }

  renderItem(resp, list, hasMasonryGrid, index) {
    let liItem: HTMLElement = this.tag_name_item === 'bam-blog-teaser-item' && !hasMasonryGrid ? document.createElement('div') : document.createElement('li');
    const liChild = document.createElement(this.tag_name_item);
    liItem.append(liChild);

    liChild['response'] = resp;

    if (this.reply_from) {
      liChild['replyFrom'] = this.reply_from;
    }

    if (this.reply_author) {
      liChild['replyAuthor'] = this.reply_author;
    }

    if (this.security_message) {
      liChild['securityMessage'] = this.security_message;
    }

    liChild['hasImageOnLoadMore'] = this.json_has_images;

    if (this.tag_name_item === 'bam-blog-teaser-item') {
      liItem.classList.add('grid-item', 'col', 'tablet:is-half', 'desktop:is-one-third');
      liChild.classList.add('bam-blog-teaser-item-wrapper');
      if (resp['image']) {
        liChild.classList.add('with-image');
      }
    }

    if (this.tag_name_list === 'bal-list') {
      liItem = liChild;
    }

    list.append(liItem);
    if (index === 0) {
      liItem.setAttribute('tabindex', '-1');
      this.newFirstEl = liItem;
    }
  }

  componentDidLoad() {
    this.btn = this.el.querySelector('bal-button');
    if (this.json_has_pages) {
      DataService.getData(this.url).then(data => {
        if (!!data && !!data.pageCount) {
          this.pageCount = parseInt(data.pageCount, 10);
        }
      });
    }

    if (this.hasMoreItems) {
      this.btn.classList.remove('hidden');
      this.toggleBtn(false);
    } else {
      this.btn.classList.add('hidden');
      this.toggleBtn(true);
    }

    this.btn &&
      this.btn.addEventListener('click', event => {
        this.handleLoadMore(event);
      });
  }

  componentWillUpdate() {
    this.toggleBtn(true);
    if (this.json_has_pages && this.pageNumber >= this.pageCount) {
      const btnParent = this.btn && this.btn.closest('bal-button');
      this.btn && this.btn.removeEventListener('click', this.handleLoadMore);
      btnParent && btnParent.remove();
    }
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
